/**
 * src/hooks/useVoiceBreadcrumbs.js — Shared voice breadcrumbs loader
 *
 * Loads voice notes for a given context. Auto-refreshes when EntityMicButton
 * finishes a recording (voice:upload_complete) AND inserts an optimistic
 * placeholder row the instant recording stops (voice:record_stop) so the
 * operator gets immediate visual confirmation.
 *
 * Primary update path: Supabase Realtime postgres_changes on voice_records.
 * Fallback path: polling on voice:upload_complete for environments where the
 * WebSocket subscription is unavailable (offline, proxy, stale token).
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { fetchVoiceNotes } from '../lib/api/voice-v2';
import { onVoiceEvent } from '../lib/voiceEvents';
import { supabase } from '../lib/supabase';
import { normalizeBreadcrumbNotes } from '../lib/voice/normalizeBreadcrumbNote';
// 2026-05-01 — STRICT_CONTEXTS now imported from the canonical scope
// taxonomy. Was hardcoded here AND in enqueue.js + voiceProcessFn.js
// with three different membership lists; CEO directive: don't duplicate
// the lists. Adding a scope = one entry in voiceScopes.VOICE_SCOPES.
import { STRICT_CONTEXTS } from '../lib/voice/voiceScopes';

function contextMatches(detail, contextType, contextId) {
  if (!contextType || !STRICT_CONTEXTS.has(contextType)) return true;
  if (detail.contextType !== contextType) return false;
  if (contextId && detail.contextId !== contextId) return false;
  return true;
}

export default function useVoiceBreadcrumbs({ contextType, contextId, limit = 5 }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [realtimeActive, setRealtimeActive] = useState(false);
  // Optimistic rows keyed by recordingId — cleared when server row arrives
  // (matched via idempotency_key) or on explicit error event.
  const [optimisticRows, setOptimisticRows] = useState([]);
  // TICKET-025 — unique per-mount suffix for the realtime channel name.
  // 5 components consume this hook (FocusView, ProcurementView,
  // ProjectWorkspace, TaskNotesSection, OverviewTab). Pre-fix they all
  // subscribed to the same channel name `voice_records:${userId}`.
  // Supabase reuses channel objects by name, so the second consumer's
  // `.on('postgres_changes', ...)` call hit a channel that was already
  // subscribed by the first consumer → throws "cannot add postgres_changes
  // callbacks for realtime:voice_records:<userId> after subscribe()".
  // ErrorBoundary then surfaces the red error UI and reloads — exact
  // CEO symptom 2026-04-27 morning ("first click into procurement says
  // procurement tab failed, blank screen with red letter, hard refreshes").
  // Random suffix fresh per mount; useRef so it stays stable across re-renders.
  const channelSuffixRef = useRef(Math.random().toString(36).slice(2, 10));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { limit };
      if (contextType) filters.contextType = contextType;
      if (contextId) filters.contextId = contextId;
      const data = await fetchVoiceNotes(filters);
      // TICKET-023: defensive normalization. The server can transiently
      // return rows with status='ready' but transcripts=[] (race between
      // setting READY status and committing voice_transcripts row, OR an
      // RLS visibility mismatch). Without this normalization, the UI shows
      // a blank breadcrumb that looks like the recording was lost. We
      // downgrade to 'transcribing' so the user sees a "still processing"
      // state until the transcript actually lands. The underlying
      // server-side data path inconsistency should also be fixed —
      // see TICKET-023 Notes for the data-layer investigation.
      setNotes(normalizeBreadcrumbNotes(data));
    } catch (err) {
      console.error('[useVoiceBreadcrumbs] load error:', err);
    } finally {
      setLoading(false);
    }
  }, [contextType, contextId, limit]);

  // Debounced reload — multiple realtime events within 300ms coalesce into
  // one network fetch. Prevents a burst during the uploaded → transcribing
  // → classifying → ready transition from firing 4 list calls in <1s.
  const debounceRef = useRef(null);
  const debouncedLoad = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      load();
    }, 300);
  }, [load]);

  useEffect(() => { load(); }, [load]);

  // Supabase Realtime subscription on voice_records.
  // Pushes over WebSocket — no polling, covers arbitrarily long finalize
  // windows (2+ minutes) that a bounded poll schedule would miss.
  useEffect(() => {
    let cancelled = false;
    let channel = null;

    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId || cancelled) return;

      channel = supabase
        .channel(`voice_records:${userId}:${channelSuffixRef.current}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'voice_records',
            filter: `user_id=eq.${userId}`,
          },
          () => {
            debouncedLoad();
          },
        )
        .subscribe((status) => {
          if (cancelled) return;
          if (status === 'SUBSCRIBED') {
            setRealtimeActive(true);
            console.info('[useVoiceBreadcrumbs] realtime SUBSCRIBED');
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            setRealtimeActive(false);
            console.warn('[useVoiceBreadcrumbs] realtime status:', status);
          }
        });
    })();

    return () => {
      cancelled = true;
      if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
      if (channel) supabase.removeChannel(channel);
    };
  }, [debouncedLoad]);

  // Optimistic insert the moment recording stops — zero network wait.
  useEffect(() => {
    const unsub = onVoiceEvent('voice:record_stop', (e) => {
      const detail = e.detail || {};
      if (!contextMatches(detail, contextType, contextId)) return;
      const { recordingId } = detail;
      if (!recordingId) return;
      const now = new Date().toISOString();
      setOptimisticRows(prev => {
        if (prev.some(r => r.idempotency_key === recordingId)) return prev;
        return [{
          id: `optimistic-${recordingId}`,
          idempotency_key: recordingId,
          status: 'transcribing',
          created_at: now,
          transcripts: [],
          classifications: [],
          actions: [],
          optimistic: true,
          context_type: contextType || null,
          context_id: contextId || null,
        }, ...prev];
      });
    });
    return () => unsub();
  }, [contextType, contextId]);

  // Refresh on upload_complete. When Realtime is active it drives all
  // subsequent updates, so we only need an immediate fetch to show the fresh
  // voice_records row (plus one short retry for the race where the INSERT
  // event fires before auth token refresh reconnects the channel).
  // When Realtime is INACTIVE, fall back to the full poll schedule covering
  // the 60s finalize window.
  useEffect(() => {
    let timers = [];
    const cancelAll = () => { timers.forEach(clearTimeout); timers = []; };
    const unsub = onVoiceEvent('voice:upload_complete', (e) => {
      const detail = e.detail || {};
      if (!contextMatches(detail, contextType, contextId)) return;
      cancelAll();
      timers.push(setTimeout(load, 0));
      timers.push(setTimeout(load, 1500));
      if (!realtimeActive) {
        timers.push(setTimeout(load, 8000));
        timers.push(setTimeout(load, 20000));
        timers.push(setTimeout(load, 40000));
        timers.push(setTimeout(load, 90000));
      }
    });
    return () => { unsub(); cancelAll(); };
  }, [contextType, contextId, load, realtimeActive]);

  // Clear optimistic row on finalize error so a stale "Processing..." doesn't linger.
  useEffect(() => {
    const unsub = onVoiceEvent('voice:finalize_error', (e) => {
      const detail = e.detail || {};
      const { recordingId } = detail;
      if (!recordingId) return;
      setOptimisticRows(prev => prev.filter(r => r.idempotency_key !== recordingId));
    });
    return () => unsub();
  }, []);

  // Drop optimistic rows once the real row arrives (matched by idempotency_key).
  useEffect(() => {
    if (optimisticRows.length === 0) return;
    const keys = new Set(notes.map(n => n.idempotency_key).filter(Boolean));
    if (optimisticRows.some(r => keys.has(r.idempotency_key))) {
      setOptimisticRows(prev => prev.filter(r => !keys.has(r.idempotency_key)));
    }
  }, [notes, optimisticRows]);

  const merged = useMemo(() => [...optimisticRows, ...notes], [optimisticRows, notes]);

  return { notes: merged, loading, refresh: load };
}
